/**
 * Shared state for the Daily Photo List page.
 *
 * Tracks the currently-viewed sheet, selected run, and recent runs list.
 * Mirrors WebsiteAuditContext pattern but simplified — no filters/sort/search
 * or vehicle data fetching yet (deferred to Batch 2).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react"

import {
  AddedVehicleRequest,
  AddedVehicleResponse,
  PhotoListRun,
  PhotoListVehicle,
  PhotoListFileStatus,
  PhotoListPreferences,
  addVehicleToList,
  removeAddedVehicle,
  listPhotoListRuns,
  fetchPhotoListVehicles,
  fetchPhotoListFileStatus,
  triggerPhotoListRun,
  fetchPhotoListPreferences,
  updatePhotoListPreferences,
} from "../api/photoList"
import { useDealerGroup } from "./DealerGroupContext"

export const PHOTO_LIST_SHEETS = [
  { slug: "toyota_new", label: "Toyota NEW" },
  { slug: "toyota_used", label: "Toyota USED" },
  { slug: "lexus_new", label: "Lexus NEW" },
  { slug: "lexus_used", label: "Lexus USED" },
  { slug: "mazda_new", label: "Mazda NEW" },
  { slug: "kia_new", label: "Kia NEW" },
  { slug: "mazda_kia_used", label: "Mazda-Kia USED" },
  { slug: "dodge_new", label: "Dodge NEW" },
  { slug: "dodge_used", label: "Dodge USED" },
  { slug: "fernley_new", label: "Fernley CDJR NEW" },
  { slug: "fernley_used", label: "Fernley CDJR USED" },
  { slug: "lee_bros_used", label: "Lee Bros USED" },
] as const

export type PhotoListSheetSlug = typeof PHOTO_LIST_SHEETS[number]["slug"]

export const DEFAULT_SHEET: PhotoListSheetSlug = "toyota_new"

interface PhotoListContextValue {
  // Run state
  currentRun: PhotoListRun | null
  recentRuns: PhotoListRun[]
  runsLoading: boolean
  runsError: string | null
  setCurrentRunId: (id: number) => void
  refreshRuns: () => void
  /** True when the viewed run is the group's newest successful list — the
   * only list that takes (or gives up) cars added by hand. */
  isLatestList: boolean
  /** Add a car by hand to today's list, then show its sheet with the car on
   * top. Rejects with the server's sentence ("That car is already on
   * Toyota USED.") so the form can show it. */
  addVehicleByHand: (request: AddedVehicleRequest) => Promise<AddedVehicleResponse>
  /** Remove a hand-added car from today's list. */
  removeVehicleAddedByHand: (vehicle: PhotoListVehicle) => Promise<void>
  // Sheet state
  activeSheet: PhotoListSheetSlug
  setActiveSheet: (slug: PhotoListSheetSlug) => void
  // Vehicle state (per-tab caching)
  vehicles: PhotoListVehicle[]
  vehiclesLoading: boolean
  vehiclesError: string | null
  // Bulk fetch for print: returns vehicles grouped by display label
  fetchSheetsForPrint: (labels: string[]) => Promise<Record<string, PhotoListVehicle[]>>
  // File status
  fileStatus: PhotoListFileStatus | null
  fileStatusLoading: boolean
  refreshFileStatus: () => void
  // Run trigger
  runPhotoList: () => Promise<PhotoListRun>
  isRunning: boolean
  // Timestamp of last successful run completion (for auto-collapse signals)
  lastRunCompletedAt: number | null
  // Export preferences
  preferences: PhotoListPreferences | null
  preferencesLoading: boolean
  loadPreferences: () => Promise<void>
  savePreferences: (prefs: PhotoListPreferences) => Promise<void>
}

const PhotoListContext = createContext<PhotoListContextValue | undefined>(undefined)

export const PhotoListProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Working surface: scoped to the active group. null = "All Groups", where the
  // page shows a "pick a group" notice instead of runs.
  const { dealerGroupId } = useDealerGroup()
  const [currentRun, setCurrentRun] = useState<PhotoListRun | null>(null)
  const [recentRuns, setRecentRuns] = useState<PhotoListRun[]>([])
  const [runsLoading, setRunsLoading] = useState(true)
  const [runsError, setRunsError] = useState<string | null>(null)
  const [activeSheet, setActiveSheetState] = useState<PhotoListSheetSlug>(DEFAULT_SHEET)

  // Vehicle cache: keyed by "runId:sheetSlug"
  const [vehicleCache, setVehicleCache] = useState<Record<string, PhotoListVehicle[]>>({})
  const [vehiclesLoading, setVehiclesLoading] = useState(false)
  const [vehiclesError, setVehiclesError] = useState<string | null>(null)

  // File status
  const [fileStatus, setFileStatus] = useState<PhotoListFileStatus | null>(null)
  const [fileStatusLoading, setFileStatusLoading] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [lastRunCompletedAt, setLastRunCompletedAt] = useState<number | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Preferences
  const [preferences, setPreferences] = useState<PhotoListPreferences | null>(null)
  const [preferencesLoading, setPreferencesLoading] = useState(false)

  const loadRuns = useCallback(async () => {
    // Under "All Groups" there's no single group to show — clear and let the
    // page render the pick-a-group notice.
    if (dealerGroupId === null) {
      setRecentRuns([])
      setCurrentRun(null)
      setVehicleCache({})
      setRunsError(null)
      setRunsLoading(false)
      return
    }
    setRunsLoading(true)
    setRunsError(null)
    try {
      // The list's first item is the latest — avoids a separate call that 404s
      // for a group with no runs yet.
      const runs = await listPhotoListRuns(30, dealerGroupId)
      setRecentRuns(runs)
      setCurrentRun(runs[0] ?? null)
      setVehicleCache({})
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load photo list runs"
      setRunsError(message)
    } finally {
      setRunsLoading(false)
    }
  }, [dealerGroupId])

  // Load runs on mount
  useEffect(() => {
    loadRuns()
  }, [loadRuns])

  // File status fetch
  const loadFileStatus = useCallback(async () => {
    // File storage is per dealer group; under "All Groups" there's nothing
    // to check (the page shows the pick-a-group notice instead).
    if (dealerGroupId === null) {
      setFileStatus(null)
      return
    }
    setFileStatusLoading(true)
    try {
      const status = await fetchPhotoListFileStatus(dealerGroupId)
      setFileStatus(status)
    } catch {
      // Silently fail — file status is informational
    } finally {
      setFileStatusLoading(false)
    }
  }, [dealerGroupId])

  // Load preferences on mount
  const loadPreferences = useCallback(async () => {
    setPreferencesLoading(true)
    try {
      const prefs = await fetchPhotoListPreferences()
      setPreferences(prefs)
    } catch {
      // Silently fail — preferences fall back to defaults in component
    } finally {
      setPreferencesLoading(false)
    }
  }, [])

  const savePreferences = useCallback(async (prefs: PhotoListPreferences) => {
    const saved = await updatePhotoListPreferences(prefs)
    setPreferences(saved)
  }, [])

  useEffect(() => {
    loadPreferences()
  }, [loadPreferences])

  // Poll file status every 30s (skip while a run is active)
  useEffect(() => {
    loadFileStatus()
    pollRef.current = setInterval(() => {
      if (!isRunning) loadFileStatus()
    }, 30_000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [loadFileStatus, isRunning])

  // Run trigger
  const runPhotoList = useCallback(async (): Promise<PhotoListRun> => {
    if (dealerGroupId === null) {
      throw new Error("Select a dealer group before running the photo list")
    }
    setIsRunning(true)
    try {
      const run = await triggerPhotoListRun(dealerGroupId)
      // Refresh runs list and switch to the new run
      await loadRuns()
      setCurrentRun(run)
      setVehicleCache({})
      // Refresh file status after run
      loadFileStatus()
      // Signal successful completion for auto-collapse
      setLastRunCompletedAt(Date.now())
      return run
    } finally {
      setIsRunning(false)
    }
  }, [dealerGroupId, loadRuns, loadFileStatus])

  const setCurrentRunId = useCallback(
    (id: number) => {
      const found = recentRuns.find((r) => r.id === id)
      if (found) {
        setCurrentRun(found)
        // Clear vehicle cache when switching runs
        setVehicleCache({})
      }
    },
    [recentRuns]
  )

  const refreshRuns = useCallback(() => {
    loadRuns()
  }, [loadRuns])

  // The server sends hand-added cars to the newest SUCCESSFUL run; the
  // newest run of all can be a failed one, so match on status too.
  const latestSuccessfulRunId = useMemo(
    () => recentRuns.find((run) => run.status === "success")?.id ?? null,
    [recentRuns],
  )
  const isLatestList = currentRun !== null && currentRun.id === latestSuccessfulRunId

  // After an add or remove: fetch the changed sheet and swap it into the
  // cache in one step — never delete it first, which showed the sheet as
  // empty until the reload landed, and for good if the reload failed
  // (review, 2026-10-12). Then reload the runs quietly (no page-wide loading
  // state) because their stored per-sheet counts moved. The change is
  // already saved, so a failed reload leaves the previous rows and counts
  // on screen until the next page load.
  const refreshAfterListChange = useCallback(
    async (runId: number, sheetSlug: string) => {
      try {
        const freshVehicles = await fetchPhotoListVehicles(runId, sheetSlug)
        setVehicleCache((prev) => ({ ...prev, [`${runId}:${sheetSlug}`]: freshVehicles }))
      } catch {
        // Keep the rows already cached rather than an empty sheet.
      }
      try {
        const runs = await listPhotoListRuns(30, dealerGroupId)
        setRecentRuns(runs)
        setCurrentRun(runs.find((run) => run.id === runId) ?? runs[0] ?? null)
      } catch {
        // Keep the runs already on screen; the next page load corrects them.
      }
    },
    [dealerGroupId],
  )

  const addVehicleByHand = useCallback(
    async (request: AddedVehicleRequest): Promise<AddedVehicleResponse> => {
      if (dealerGroupId === null) {
        throw new Error("Select a dealer group before adding a car")
      }
      const added = await addVehicleToList(request, dealerGroupId)
      await refreshAfterListChange(added.run_id, added.sheet)
      // Show the car where it landed — the person may have picked another
      // sheet than the one on screen.
      setActiveSheetState(added.sheet as PhotoListSheetSlug)
      return added
    },
    [dealerGroupId, refreshAfterListChange],
  )

  const removeVehicleAddedByHand = useCallback(
    async (vehicle: PhotoListVehicle): Promise<void> => {
      // Throw rather than return: the notes modal closes on success, and a
      // silent return read as "removed" while nothing was sent.
      if (dealerGroupId === null || currentRun === null) {
        throw new Error("The list isn't loaded. Reload the page and try again.")
      }
      await removeAddedVehicle(vehicle.id, dealerGroupId)
      const sheetSlug =
        PHOTO_LIST_SHEETS.find((sheet) => sheet.label === vehicle.sheet)?.slug ?? activeSheet
      await refreshAfterListChange(currentRun.id, sheetSlug)
    },
    [dealerGroupId, currentRun, activeSheet, refreshAfterListChange],
  )

  const setActiveSheet = useCallback((slug: PhotoListSheetSlug) => {
    setActiveSheetState(slug)
  }, [])

  // Fetch vehicles for the active sheet when run or sheet changes
  const cacheKey = currentRun ? `${currentRun.id}:${activeSheet}` : null

  useEffect(() => {
    if (!currentRun || !cacheKey) return
    // Already cached
    if (vehicleCache[cacheKey]) return

    let cancelled = false
    setVehiclesLoading(true)
    setVehiclesError(null)

    fetchPhotoListVehicles(currentRun.id, activeSheet)
      .then((data) => {
        if (!cancelled) {
          setVehicleCache((prev) => ({ ...prev, [cacheKey]: data }))
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setVehiclesError(
            err instanceof Error ? err.message : "Failed to load vehicles",
          )
        }
      })
      .finally(() => {
        if (!cancelled) setVehiclesLoading(false)
      })

    return () => { cancelled = true }
  }, [currentRun, activeSheet, cacheKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const vehicles = useMemo(
    () => (cacheKey ? vehicleCache[cacheKey] ?? [] : []),
    [cacheKey, vehicleCache],
  )

  // Bulk-fetch vehicles for multiple sheets (used by Print).
  // Returns a map of display label → vehicles, leveraging the cache.
  const fetchSheetsForPrint = useCallback(
    async (labels: string[]): Promise<Record<string, PhotoListVehicle[]>> => {
      if (!currentRun) return {}

      const labelToSlug: Record<string, string> = {}
      for (const s of PHOTO_LIST_SHEETS) {
        labelToSlug[s.label] = s.slug
      }

      const result: Record<string, PhotoListVehicle[]> = {}
      const toFetch: { label: string; slug: string }[] = []

      for (const label of labels) {
        const slug = labelToSlug[label]
        if (!slug) continue
        const key = `${currentRun.id}:${slug}`
        if (vehicleCache[key]) {
          result[label] = vehicleCache[key]
        } else {
          toFetch.push({ label, slug })
        }
      }

      if (toFetch.length > 0) {
        const fetched = await Promise.all(
          toFetch.map(async ({ label, slug }) => {
            const data = await fetchPhotoListVehicles(currentRun.id, slug)
            return { label, slug, data }
          }),
        )
        const cacheUpdates: Record<string, PhotoListVehicle[]> = {}
        for (const { label, slug, data } of fetched) {
          result[label] = data
          cacheUpdates[`${currentRun.id}:${slug}`] = data
        }
        // Update cache so subsequent tab switches don't re-fetch
        setVehicleCache((prev) => ({ ...prev, ...cacheUpdates }))
      }

      return result
    },
    [currentRun, vehicleCache],
  )

  const value = useMemo<PhotoListContextValue>(
    () => ({
      currentRun,
      recentRuns,
      runsLoading,
      runsError,
      setCurrentRunId,
      refreshRuns,
      isLatestList,
      addVehicleByHand,
      removeVehicleAddedByHand,
      activeSheet,
      setActiveSheet,
      vehicles,
      vehiclesLoading: vehiclesLoading && !vehicleCache[cacheKey ?? ""],
      vehiclesError,
      fetchSheetsForPrint,
      fileStatus,
      fileStatusLoading,
      refreshFileStatus: loadFileStatus,
      runPhotoList,
      isRunning,
      lastRunCompletedAt,
      preferences,
      preferencesLoading,
      loadPreferences,
      savePreferences,
    }),
    [
      currentRun,
      recentRuns,
      runsLoading,
      runsError,
      setCurrentRunId,
      refreshRuns,
      isLatestList,
      addVehicleByHand,
      removeVehicleAddedByHand,
      activeSheet,
      setActiveSheet,
      vehicles,
      vehiclesLoading,
      vehiclesError,
      vehicleCache,
      cacheKey,
      fetchSheetsForPrint,
      fileStatus,
      fileStatusLoading,
      loadFileStatus,
      runPhotoList,
      isRunning,
      lastRunCompletedAt,
      preferences,
      preferencesLoading,
      loadPreferences,
      savePreferences,
    ]
  )

  return <PhotoListContext.Provider value={value}>{children}</PhotoListContext.Provider>
}

export const usePhotoList = (): PhotoListContextValue => {
  const ctx = useContext(PhotoListContext)
  if (!ctx) {
    throw new Error("usePhotoList must be used within PhotoListProvider")
  }
  return ctx
}
